import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { AnimatePresence, motion } from 'motion/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t, chips, money, dateTime, duration, errorText } from '../i18n/index.ts';
import { commitmentForJoin, SHOW_FAIRNESS } from '../lib/fairness.ts';
import { useSoundSetting } from '../lib/sound.ts';
import { useTournamentPresence } from '../lib/presence.ts';
import { useShowdownPresenting } from './Showdown.ts';
import { WinnerCelebration } from '../components/WinnerCelebration.tsx';
import { markFinishRevealed, useFinishRevealed } from '../lib/finishReveal.ts';
import { FairnessSection } from './Fairness.tsx';
import { api } from '../lib/api.ts';
import { Avatar, Dialog, ErrorView, Field, Loading, Money, Status, useToast } from '../components/ui.tsx';
import type { TournamentListItem, tournamentDetail } from '../../server/domain/tournaments.ts';
import type { Terms } from '../../server/domain/tournamentStore.ts';
import { useSession } from '../lib/session.tsx';
import { TabBar, UnitToggle } from '../components/TabBar.tsx';
import { useAmountUnit } from '../lib/amounts.ts';import type { tournamentInvites } from '../../server/domain/invites.ts';


export type TournamentDetailData = ReturnType<typeof tournamentDetail>;

type ListFilter = 'mine' | 'open' | 'running' | 'finished' | 'all';

function matches(x: TournamentListItem, f: ListFilter): boolean {
  switch (f) {
    case 'mine':
      return x.myStatus !== null && x.myStatus !== 'withdrawn' && x.status !== 'cancelled';
    case 'open':
      return x.status === 'registration';
    case 'running':
      return x.status === 'running' || x.status === 'paused';
    case 'finished':
      return x.status === 'finished' || x.status === 'cancelled';
    default:
      return true;
  }
}

/** Sort: my pending decisions first, then running, open, drafts, finished. */
const ORDER: Record<string, number> = { running: 1, paused: 1, registration: 2, draft: 3, finished: 4, cancelled: 5 };

/** "Pudonnut" says nothing about how it went: a finished player is shown with their place. */
function entryText(status: string, place: { from: number; to: number } | null): string {
  if ((status === 'eliminated' || status === 'winner') && place) {
    return place.from === 1 && place.to === 1 ? t('tournament.entryStatus.winner') : t('tournament.placeText', { place: place.from === place.to ? `${place.from}.` : `${place.from}.–${place.to}.` });
  }
  return t(`tournament.entryStatus.${status as 'playing'}`);
}

export function TournamentList() {
  const q = useQuery({ queryKey: ['tournaments'], queryFn: () => api.get<{ tournaments: TournamentListItem[] }>('/api/tournaments') });
  const { me } = useSession();
  // null until the player picks a tab: then the first tab that has something in it is shown.
  const [filter, setFilter] = useState<ListFilter | null>(null);
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  const all = q.data.tournaments;
  const filters: [ListFilter, string][] = [
    ['open', t('tournament.filters.open')],
    ['mine', t('tournament.filters.mine')],
    ['running', t('tournament.filters.running')],
    ['finished', t('tournament.filters.finished')],
    ['all', t('tournament.filters.all')],
  ];
  const effective = filter ?? (['open', 'mine'] as const).find((f) => all.some((x) => matches(x, f))) ?? 'all';
  const list = all
    .filter((x) => matches(x, effective))
    .sort((a, b) => Number(b.isMyTurn) - Number(a.isMyTurn) || (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9));
  return (
    <div className="page">
      <div className="section-head">
        <h1>{t('tournament.list')}</h1>
        <Link className="btn btn-primary" to="/turnaukset/uusi">
          {t('admin.newTournament')}
        </Link>
      </div>
      <TabBar
        group="tournament-filter"
        label={t('tournament.list')}
        active={effective}
        items={filters.map(([key, label]) => ({
          key,
          label: (
            <>
              {label} <span className="hint num">({all.filter((x) => matches(x, key)).length})</span>
            </>
          ),
          onSelect: () => setFilter(key),
        }))}
      />
      {list.length === 0 ? (
        <div className="rows">
          <p className="empty">{all.length === 0 ? t('tournament.empty') : t('tournament.filters.none')}</p>
        </div>
      ) : (
        <ul className="rows tlist">
          <li className="tlist-head" aria-hidden="true">
            <span>{t('tournament.columns.name')}</span>
            <span>{t('tournament.columns.status')}</span>
            <span>{t('tournament.columns.me')}</span>
            <span className="r">{t('tournament.columns.seats')}</span>
            <span className="r">{t('tournament.columns.buyIn')}</span>
          </li>
          {list.map((x) => (
            <li key={x.id}>
              <Link className="row tlist-row" to={x.status === 'running' || x.status === 'paused' ? `/turnaukset/${x.id}/poyta` : `/turnaukset/${x.id}`}>
                <span className="tlist-name">
                  <span className="row-title">{x.name}</span>
                  <span className="row-sub">
                    {chips(x.startingStack)} {t('tournament.startingStack').toLowerCase()}, {t('tournament.turnTime').toLowerCase()} {duration(x.turnSeconds * 1000)}
                  </span>
                </span>
                <span>
                  <Status kind={x.status}>{t(`tournament.status.${x.status}`)}</Status>
                </span>
                <span>
                  {x.isMyTurn ? (
                    <Status kind="turn">{t('home.yourTurn')}</Status>
                  ) : x.myStatus && x.myStatus !== 'withdrawn' ? (
                    <span className="row-sub">
                      {x.myStatus === 'registered' && x.myTermsCurrent === false ? t('tournament.termsPending') : entryText(x.myStatus, x.myPlace)}
                    </span>
                  ) : (
                    <span className="row-sub" aria-hidden="true">–</span>
                  )}
                </span>
                <span className="r num">{t('tournament.seats', { registered: x.registered, capacity: x.capacity })}</span>
                <span className="r">
                  <Money cents={x.buyInCents} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TermRow({ label, value, previous }: { label: string; value: ReactNode; previous?: ReactNode | null }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>
        <span className={previous !== undefined && previous !== null ? 'changed' : undefined}>{value}</span>
        {previous !== undefined && previous !== null && <span className="hint"> ({t('tournament.was', { value: String(previous) })})</span>}
      </dd>
    </>
  );
}

export function TermsView({ terms, previous, prizePreview, reminderMinutes }: { terms: Terms; previous: Terms | null; prizePreview: number[]; reminderMinutes: number[] }) {
  const [allLevels, setAllLevels] = useState(false);
  const blindsChanged = !!previous && JSON.stringify(previous.blindLevels) !== JSON.stringify(terms.blindLevels);
  const levels = allLevels || blindsChanged ? terms.blindLevels : terms.blindLevels.slice(0, 8);
  const diff = <K extends keyof Terms>(k: K, fmt: (v: Terms[K]) => string) =>
    previous && JSON.stringify(previous[k]) !== JSON.stringify(terms[k]) ? fmt(previous[k]) : null;
  return (
    <>
      <dl className="kv">
        <TermRow label={t('tournament.buyIn')} value={money(terms.buyInCents)} previous={diff('buyInCents', money)} />
        <TermRow label={t('tournament.capacity')} value={terms.capacity} previous={diff('capacity', String)} />
        <TermRow label={t('tournament.prizePool')} value={money(terms.prizePoolCents)} previous={diff('prizePoolCents', money)} />
        <TermRow label={t('tournament.startingStack')} value={chips(terms.startingStack)} previous={diff('startingStack', chips)} />
        <TermRow label={t('tournament.turnTime')} value={duration(terms.turnSeconds * 1000)} previous={diff('turnSeconds', (s) => duration(s * 1000))} />
        <TermRow label={t('tournament.ante')} value={t(`tournament.anteMode.${terms.anteMode}`)} previous={diff('anteMode', (m) => t(`tournament.anteMode.${m}`))} />
        <TermRow
          label={t('tournament.away')}
          value={t('tournament.awayValue', { n: terms.awayAfterTimeouts })}
          previous={diff('awayAfterTimeouts', (n) => t('tournament.awayValue', { n }))}
        />
        {reminderMinutes.length > 0 && (
          <TermRow label={t('tournament.reminders')} value={t('tournament.remindersValue', { list: reminderMinutes.map((m) => duration(m * 60_000)).join(', ') })} />
        )}
      </dl>
      <p className="hint" style={{ marginTop: 12 }}>
        {t('tournament.timeoutRule')}
      </p>

      <h3 style={{ marginTop: 20 }}>{t('tournament.payout')}</h3>
      {previous && JSON.stringify(previous.payoutBp) !== JSON.stringify(terms.payoutBp) && (
        <p className="changed">{t('tournament.was', { value: previous.payoutBp.map((b) => `${b / 100} %`).join(' / ') })}</p>
      )}
      <table className="data-table">
        <thead>
          <tr>
            <th>{t('tournament.place')}</th>
            <th className="r">{t('tournament.share')}</th>
            <th className="r">{t('tournament.prize')}</th>
          </tr>
        </thead>
        <tbody>
          {terms.payoutBp.map((bp, i) => (
            <tr key={i}>
              <td>{i + 1}.</td>
              <td className="r">{(bp / 100).toLocaleString('fi-FI')} %</td>
              <td className="r">{money(prizePreview[i] ?? 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="hint">{t('tournament.rounding')}</p>

      <h3 style={{ marginTop: 20 }}>{t('tournament.blinds')}</h3>
      <p className="hint">{t('tournament.blindsHelp', { n: terms.handsPerLevel })}</p>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('tournament.level')}</th>
              <th className="r">{t('tournament.sb')}</th>
              <th className="r">{t('tournament.bb')}</th>
              {terms.anteMode !== 'none' && <th className="r">{t('tournament.ante')}</th>}
            </tr>
          </thead>
          <tbody>
            {levels.map((l, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td className="r">{chips(l.sb)}</td>
                <td className="r">{chips(l.bb)}</td>
                {terms.anteMode !== 'none' && <td className="r">{chips(l.ante)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {terms.blindLevels.length > 8 && !blindsChanged && (
        <button type="button" className="btn btn-small" style={{ marginTop: 8 }} aria-expanded={allLevels} onClick={() => setAllLevels((v) => !v)}>
          {allLevels ? t('tournament.showFewerLevels') : t('tournament.showAllLevels', { n: terms.blindLevels.length })}
        </button>
      )}
      {blindsChanged && <p className="changed">{t('tournament.was', { value: previous!.blindLevels.map((l) => `${l.sb}/${l.bb}`).join(', ') })}</p>}
      <p className="hint">{t('tournament.chipsNotMoney')}</p>
    </>
  );
}

type TournamentTab = 'info' | 'table' | 'results';

function tabFromPath(pathname: string): TournamentTab {
  if (/\/poyta\/?$/.test(pathname)) return 'table';
  if (/\/(tulokset|jaot)(\/|$)/.test(pathname)) return 'results';
  return 'info';
}

/**
 * Shared frame for a tournament's pages. The header and tabs stay mounted and keep the same
 * width while only the tab content changes, so nothing jumps when switching tabs.
 */
const celebratedKey = (tournamentId: string) => `uh.celebrated.${tournamentId}`;

/**
 * The winner's screen, once per device for everyone who opens a finished tournament. When the
 * tournament ends while the page is open, it waits for the last hand to be played out first.
 */
function useWinnerCelebration(d: TournamentDetailData | undefined) {
  const [sound] = useSoundSetting();
  const [open, setOpen] = useState(false);
  const sawRunning = useRef(false);
  const finished = d?.status === 'finished';
  const results = useQuery({
    queryKey: ['results', d?.id],
    queryFn: () =>
      api.get<{ standings: { user: { displayName: string; avatarUrl: string | null }; placeFrom: number | null; placeTo: number | null; prizeCents: number | null }[] }>(
        `/api/tournaments/${d!.id}/results`,
      ),
    enabled: finished,
  });
  const presenting = useShowdownPresenting();
  const [waited, setWaited] = useState(false);
  if (d && d.status === 'running') sawRunning.current = true;
  const ready = !!d && finished && !!results.data;
  // Opened after the end: nothing to wait for. Ended while watching: once the winner's screen
  // opens (below), or after a while in case it never can (results not loading).
  useEffect(() => {
    if (!d || !finished) return;
    if (!sawRunning.current) {
      markFinishRevealed(d.id);
      return;
    }
    const timer = window.setTimeout(() => markFinishRevealed(d.id), 30_000);
    return () => window.clearTimeout(timer);
  }, [d?.id, finished]);
  useEffect(() => {
    if (open && d) markFinishRevealed(d.id);
  }, [open, d?.id]);
  // Ended while watching: give the table a moment to start playing out the last hand, then wait
  // for that to finish. Opened later: show it right away.
  useEffect(() => {
    if (!ready) return;
    const timer = window.setTimeout(() => setWaited(true), sawRunning.current ? 1500 : 300);
    return () => window.clearTimeout(timer);
  }, [ready]);
  useEffect(() => {
    if (!ready || !waited || presenting || open) return;
    try {
      if (localStorage.getItem(celebratedKey(d!.id))) {
        // Already celebrated on this device: the end can show once the last hand has.
        markFinishRevealed(d!.id);
        return;
      }
    } catch {
      // no storage: show it this time
    }
    // A moment to see who won the last hand first.
    const timer = window.setTimeout(() => setOpen(true), sawRunning.current ? 2200 : 0);
    return () => window.clearTimeout(timer);
  }, [ready, waited, presenting, open, d]);
  if (!open || !d || !results.data) return null;
  const podium = results.data.standings
    .filter((s) => s.placeFrom !== null && s.placeFrom <= 3)
    .map((s) => ({
      place: s.placeFrom!,
      label: s.placeTo && s.placeTo !== s.placeFrom ? `${s.placeFrom}.–${s.placeTo}.` : `${s.placeFrom}.`,
      name: s.user.displayName,
      avatarUrl: s.user.avatarUrl,
      prizeCents: s.prizeCents ?? 0,
    }));
  if (podium.length === 0) return null;
  const close = () => {
    setOpen(false);
    try {
      localStorage.setItem(celebratedKey(d.id), String(Date.now()));
    } catch {
      // fine: it may show again next time
    }
  };
  return <WinnerCelebration key="winner" tournamentId={d.id} tournamentName={d.name} podium={podium} sound={sound} onClose={close} />;
}

export function TournamentLayout() {
  const { id } = useParams();
  const location = useLocation();
  const [unit, setUnit] = useAmountUnit();
  const q = useQuery({ queryKey: ['tournament', id], queryFn: () => api.get<TournamentDetailData>(`/api/tournaments/${id}`) });
  const tab = tabFromPath(location.pathname);
  const celebration = useWinnerCelebration(q.data);
  const finishRevealed = useFinishRevealed(id);
  useTournamentPresence(q.data && (q.data.status === 'running' || q.data.status === 'paused') ? id : undefined);
  if (q.isPending)
    return (
      <div className="tpage">
        <Loading />
      </div>
    );
  if (q.isError)
    return (
      <div className="tpage">
        <ErrorView error={q.error} onRetry={() => q.refetch()} />
      </div>
    );
  const d = q.data;
  const started = d.status !== 'draft' && d.status !== 'registration';
  const items = [
    { key: 'info', label: t('tournament.info'), to: `/turnaukset/${d.id}` },
    ...(started
      ? [
          { key: 'table', label: t('tournament.goTable'), to: `/turnaukset/${d.id}/poyta` },
          { key: 'results', label: t('tournament.results'), to: `/turnaukset/${d.id}/tulokset` },
        ]
      : []),
  ];
  return (
    <div className="tpage">
      <header className="tpage-head">
        <h1>{d.name}</h1>
        <div className="tpage-actions">
          {started && <UnitToggle unit={unit} onChange={(u) => void setUnit(u)} />}
          {/* Until the winner is shown, a tournament that just ended still reads as running. */}
          {d.status === 'finished' && !finishRevealed ? (
            <Status kind="running">{t('tournament.status.running')}</Status>
          ) : (
            <Status kind={d.status}>{t(`tournament.status.${d.status}`)}</Status>
          )}
        </div>
      </header>
      <TabBar group="tournament" label={t('tournament.info')} active={tab} items={items} />
      <AnimatePresence>{celebration}</AnimatePresence>
      <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
        <Outlet />
      </motion.div>
    </div>
  );
}

type InvitesData = ReturnType<typeof tournamentInvites>;

/** Who has been invited, and a way to invite more (each player at most once, by anyone). */
function InviteSection({ tournamentId }: { tournamentId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['invites', tournamentId], queryFn: () => api.get<InvitesData>(`/api/tournaments/${tournamentId}/invites`) });
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const send = useMutation({
    mutationFn: () => {
      const everyone = q.data !== undefined && chosen.size === q.data.invitable.length;
      return api.post<{ invited: number; skipped: number }>(`/api/tournaments/${tournamentId}/invites`, everyone ? { all: true } : { userIds: [...chosen] });
    },
    onSuccess: (res) => {
      toast(t('invites.sent', { n: res.invited }));
      setOpen(false);
      setChosen(new Set());
    },
    onError: (e) => toast(errorText(e), 'error'),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['invites', tournamentId] }),
  });
  if (!q.data || (!q.data.canInvite && q.data.invited.length === 0)) return null;
  const { invitable, invited, canInvite } = q.data;
  const allChosen = invitable.length > 0 && chosen.size === invitable.length;
  const toggle = (id: string) => {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setChosen(next);
  };
  return (
    <section className="panel invite-section" style={{ marginTop: 16 }} aria-labelledby="invites-h">
      <div className="section-head">
        <h2 id="invites-h">{t('invites.title')}</h2>
        {canInvite && invitable.length > 0 && (
          <button className="btn btn-small btn-primary" onClick={() => setOpen(true)}>
            {t('invites.invite')}
          </button>
        )}
      </div>
      {invited.length === 0 ? (
        <p className="hint">{t('invites.none')}</p>
      ) : (
        <ul className="invite-list">
          {invited.map((i) => (
            <li key={i.user.id}>
              <Avatar name={i.user.displayName} url={i.user.avatarUrl} size="s" />
              <span className="invite-name">{i.user.displayName}</span>
              <span className="hint">{t('invites.by', { name: i.by.displayName })}</span>
            </li>
          ))}
        </ul>
      )}
      {canInvite && invitable.length === 0 && <p className="hint">{t('invites.everyoneInvited')}</p>}
      {open && (
        <Dialog title={t('invites.dialogTitle')} onClose={() => setOpen(false)}>
          <p className="hint">{t('invites.dialogHelp')}</p>
          <label className="check invite-all">
            <input type="checkbox" checked={allChosen} onChange={() => setChosen(allChosen ? new Set() : new Set(invitable.map((u) => u.id)))} />
            <span>{t('invites.all', { n: invitable.length })}</span>
          </label>
          <div className="invite-choices" role="group" aria-label={t('invites.dialogTitle')}>
            {invitable.map((u) => (
              <label key={u.id} className="check invite-choice">
                <input type="checkbox" checked={chosen.has(u.id)} onChange={() => toggle(u.id)} />
                <Avatar name={u.displayName} url={u.avatarUrl} size="s" />
                <span>{u.displayName}</span>
              </label>
            ))}
          </div>
          <div className="btn-row">
            <button className="btn btn-primary" disabled={chosen.size === 0 || send.isPending} onClick={() => send.mutate()}>
              {t('invites.send', { n: chosen.size })}
            </button>
            <button className="btn" data-close onClick={() => setOpen(false)}>
              {t('app.cancel')}
            </button>
          </div>
        </Dialog>
      )}
    </section>
  );
}

/** The organizer's own tools: edit, publish or delete a draft, call off before the start. */
function OrganizerPanel({ d }: { d: TournamentDetailData }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<null | 'publish' | 'delete' | 'cancel'>(null);
  const [reason, setReason] = useState('');
  const action = useMutation({
    mutationFn: async (kind: 'publish' | 'delete' | 'cancel') => {
      if (kind === 'delete') return api.del(`/api/tournaments/${d.id}`);
      if (kind === 'publish') return api.post(`/api/tournaments/${d.id}/publish`);
      return api.post(`/api/tournaments/${d.id}/cancel`, { reason });
    },
    onSuccess: (_r, kind) => {
      toast(kind === 'publish' ? t('organizer.published') : kind === 'delete' ? t('organizer.deleted') : t('organizer.cancelled'));
      setDialog(null);
      setReason('');
      if (kind === 'delete') navigate('/turnaukset', { replace: true });
    },
    onError: (e) => toast(errorText(e), 'error'),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tournament', d.id] });
      void qc.invalidateQueries({ queryKey: ['tournaments'] });
    },
  });
  const editable = d.status === 'draft' || d.status === 'registration';
  if (!editable) return null;
  return (
    <section className="panel organizer-panel" aria-labelledby="org-h">
      <h2 id="org-h">{d.status === 'draft' ? t('organizer.draftTitle') : t('organizer.title')}</h2>
      <p className="hint">{d.status === 'draft' ? t('organizer.draftHelp') : t('organizer.registrationHelp')}</p>
      <div className="btn-row">
        {d.status === 'draft' && (
          <button className="btn btn-primary" onClick={() => setDialog('publish')}>
            {t('admin.publish')}
          </button>
        )}
        <Link className="btn" to={`/turnaukset/${d.id}/muokkaa`}>
          {t('admin.edit')}
        </Link>
        {d.status === 'draft' ? (
          <button className="btn btn-ghost" onClick={() => setDialog('delete')}>
            {t('admin.deleteDraft')}
          </button>
        ) : (
          <button className="btn btn-ghost" onClick={() => setDialog('cancel')}>
            {t('admin.cancelT')}
          </button>
        )}
      </div>
      {dialog && (
        <Dialog
          title={dialog === 'publish' ? t('admin.publish') : dialog === 'delete' ? t('admin.deleteDraft') : t('admin.cancelT')}
          onClose={() => setDialog(null)}
        >
          <p>{dialog === 'publish' ? t('admin.publishConfirm') : dialog === 'delete' ? t('organizer.deleteConfirm') : t('organizer.cancelHelp')}</p>
          {dialog === 'cancel' && (
            <Field label={t('admin.reason')} hint={t('admin.reasonHelp')} htmlFor="org-reason">
              <input id="org-reason" className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
            </Field>
          )}
          <div className="btn-row">
            <button
              className={`btn ${dialog === 'publish' ? 'btn-primary' : 'btn-danger'}`}
              disabled={action.isPending || (dialog === 'cancel' && reason.trim().length < 3)}
              onClick={() => action.mutate(dialog)}
            >
              {t('app.confirm')}
            </button>
            <button className="btn" data-close onClick={() => setDialog(null)}>
              {t('app.cancel')}
            </button>
          </div>
        </Dialog>
      )}
    </section>
  );
}

export function TournamentDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const { me } = useSession();
  const q = useQuery({ queryKey: ['tournament', id], queryFn: () => api.get<TournamentDetailData>(`/api/tournaments/${id}`) });
  const [accepted, setAccepted] = useState(false);
  const [moneyAccepted, setMoneyAccepted] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const join = useMutation({
    mutationFn: (d: TournamentDetailData) =>
      api.post<{ started: boolean }>(`/api/tournaments/${id}/join`, {
        termsVersion: d.termsVersion,
        termsHash: d.termsHash,
        accept: true,
        realMoneyAccepted: d.terms.buyInCents > 0 ? moneyAccepted : undefined,
        // Provably fair: only the commitment to this browser's secret seed is sent.
        ...(d.fairness && me && { fairnessCommitment: commitmentForJoin(d.id, me.id) }),
      }),
    onSuccess: (res) => {
      toast(res.started ? t('tournament.joinedStarted') : t('tournament.joined'));
      setAccepted(false);
      setMoneyAccepted(false);
      setJoinOpen(false);
    },
    onError: (err) => toast(errorText(err), 'error'),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tournament', id] });
      void qc.invalidateQueries({ queryKey: ['home'] });
    },
  });
  const withdraw = useMutation({
    mutationFn: () => api.post(`/api/tournaments/${id}/withdraw`),
    onError: (err) => toast(errorText(err), 'error'),
    onSettled: () => {
      setConfirmWithdraw(false);
      void qc.invalidateQueries({ queryKey: ['tournament', id] });
    },
  });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;
  const my = d.my;
  const registeredCount = d.entrants.filter((e) => e.status === 'registered').length;
  const canJoin = d.status === 'registration' && (!my || my.status === 'withdrawn' || (my.status === 'registered' && !my.termsCurrent));
  const full = registeredCount >= d.terms.capacity && !(my?.status === 'registered');
  const paid = d.terms.buyInCents > 0;
  return (
    <div>
      {d.canManage && <OrganizerPanel d={d} />}
      {d.organizer && !d.canManage && <p className="hint">{t('tournament.organizer', { name: d.organizer.displayName })}</p>}
      {d.description && <p style={{ whiteSpace: 'pre-wrap' }}>{d.description}</p>}
      {d.status === 'cancelled' && <div className="alert alert-error">{t('tournament.cancelled', { reason: d.cancelReason ?? '' })}</div>}
      {d.status === 'paused' && <div className="alert alert-warn">{t('tournament.pausedBanner', { reason: d.pauseReason ?? '' })}</div>}
      {my?.status === 'registered' && !my.termsCurrent && (
        <div className="alert alert-warn" role="alert">
          <strong>{t('tournament.termsChanged')}</strong>
          <p style={{ margin: '4px 0 0' }}>{t('tournament.termsChangedHelp')}</p>
        </div>
      )}
      <div className="grid-2" style={{ alignItems: 'start' }}>
        <section className="panel" aria-labelledby="terms-h">
          <h2 id="terms-h">{t('tournament.rulesTitle')}</h2>
          <TermsView terms={d.terms} previous={d.previousTerms} prizePreview={d.prizePreview} reminderMinutes={d.reminderMinutes} />
        </section>
        <div>
          <section className="panel" aria-labelledby="join-h">
            <h2 id="join-h">
              {d.status === 'registration'
                ? t('tournament.seats', { registered: registeredCount, capacity: d.terms.capacity })
                : t('tournament.playerCount', { n: d.entrants.filter((e) => e.status !== 'withdrawn').length })}
            </h2>
            <p className="hint">{t('tournament.moneyNote')}</p>
            {d.registrationDeadline && (
              <p>
                {t('tournament.registrationDeadline')}: <strong>{dateTime(d.registrationDeadline)}</strong>
              </p>
            )}
            {canJoin && !full && (
              <button className="btn btn-primary btn-block join-inline" disabled={join.isPending} onClick={() => setJoinOpen(true)}>
                {my?.status === 'registered' ? t('tournament.acceptNew') : t('tournament.join')}
              </button>
            )}
            {canJoin && full && <p className="alert">{t('errors.tournament_full')}</p>}
            {my?.status === 'registered' && d.status === 'registration' && (
              <>
                {my.termsCurrent && <p className="alert alert-ok">{t('tournament.joined')}</p>}
                <button className="btn btn-danger btn-block" style={{ marginTop: 10 }} onClick={() => setConfirmWithdraw(true)}>
                  {t('tournament.withdraw')}
                </button>
              </>
            )}
            {(d.status === 'running' || d.status === 'paused') && (
              <Link className="btn btn-primary btn-block" to={`/turnaukset/${d.id}/poyta`}>
                {t('tournament.goTable')}
              </Link>
            )}
            {d.status === 'finished' && (
              <Link className="btn btn-primary btn-block" to={`/turnaukset/${d.id}/tulokset`}>
                {t('tournament.results')}
              </Link>
            )}
          </section>
          {d.status === 'registration' && <InviteSection tournamentId={d.id} />}
          <section className="section" style={{ marginTop: 20 }} aria-labelledby="entrants-h">
            <h2 id="entrants-h">{t('tournament.entrants')}</h2>
            {d.entrants.length === 0 ? (
              <div className="rows">
                <p className="empty">{t('tournament.noEntrants')}</p>
              </div>
            ) : (
              <ul className="rows">
                {d.entrants.map((e) => (
                  <li key={e.user.id} className="row">
                    <Avatar name={e.user.displayName} url={e.user.avatarUrl} size="s" />
                    <div className="row-main">
                      <div className="row-title">{e.user.displayName}</div>
                    </div>
                    {e.status === 'registered' && !e.termsCurrent ? (
                      <span className="row-sub">{t('tournament.termsPending')}</span>
                    ) : (
                      <span className="row-sub">{entryText(e.status, e.place)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
          {d.channelId && (
            <Link className="btn" to={`/keskustelu/${d.channelId}`}>
              {t('tournament.chat')}
            </Link>
          )}
          {me?.role === 'superadmin' && (
            <Link className="btn" style={{ marginLeft: 8 }} to={`/yllapito/turnaukset/${d.id}`}>
              {t('nav.admin')}
            </Link>
          )}
        </div>
      </div>
      {SHOW_FAIRNESS && d.status !== 'draft' && (
        <div className="panel" style={{ marginTop: 16 }}>
          <FairnessSection tournamentId={d.id} />
        </div>
      )}
      {/* Phones: the rules are long, so joining is always at hand at the bottom of the screen. */}
      {canJoin && !full && !joinOpen && (
        <div className="join-bar">
          <button className="btn btn-primary btn-block" disabled={join.isPending} onClick={() => setJoinOpen(true)}>
            {my?.status === 'registered' ? t('tournament.acceptNew') : t('tournament.joinWithPrice', { price: money(d.terms.buyInCents) })}
          </button>
          <span className="hint num">{t('tournament.seats', { registered: registeredCount, capacity: d.terms.capacity })}</span>
        </div>
      )}
      {joinOpen && (
        <Dialog title={t('tournament.joinDialogTitle')} onClose={() => setJoinOpen(false)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (accepted && (!paid || moneyAccepted) && !join.isPending) join.mutate(d);
            }}
          >
            <p>
              <strong>{d.name}</strong>
              <br />
              {t('tournament.joinSummary', { buyIn: money(d.terms.buyInCents), pool: money(d.terms.prizePoolCents), capacity: d.terms.capacity })}
            </p>
            {paid && (
              <div className="alert alert-warn" role="note">
                <strong>{t('tournament.realMoneyTitle')}</strong>
                <p style={{ margin: '6px 0 0' }}>{t('tournament.realMoneyText', { buyIn: money(d.terms.buyInCents) })}</p>
              </div>
            )}
            <label className="check">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
              <span>
                {paid
                  ? t('tournament.joinConfirm', { buyIn: money(d.terms.buyInCents), version: d.termsVersion })
                  : t('tournament.joinFreeConfirm', { version: d.termsVersion })}
              </span>
            </label>
            {paid && (
              <label className="check">
                <input type="checkbox" checked={moneyAccepted} onChange={(e) => setMoneyAccepted(e.target.checked)} />
                <span>{t('tournament.realMoneyAccept')}</span>
              </label>
            )}
            <div className="btn-row" style={{ marginTop: 12 }}>
              <button className="btn btn-primary" disabled={!accepted || (paid && !moneyAccepted) || join.isPending}>
                {t('tournament.joinAndAccept')}
              </button>
              <button type="button" className="btn" data-close onClick={() => setJoinOpen(false)}>
                {t('app.cancel')}
              </button>
            </div>
          </form>
        </Dialog>
      )}
      {confirmWithdraw && (
        <Dialog title={t('tournament.withdraw')} onClose={() => setConfirmWithdraw(false)}>
          <p>{t('tournament.withdrawConfirm')}</p>
          <div className="btn-row">
            <button className="btn btn-danger" disabled={withdraw.isPending} onClick={() => withdraw.mutate()}>
              {t('tournament.withdraw')}
            </button>
            <button className="btn" data-close onClick={() => setConfirmWithdraw(false)}>
              {t('app.cancel')}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
